import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Briefcase,
  LayoutDashboard,
  AlertCircle,
  Star,
  Plus,
  Search,
  Users,
  CheckCircle,
  Circle,
  Snowflake,
  RefreshCw,
  UserPlus,
  UserMinus,
  ChevronDown,
  ChevronUp,
  Building,
  Megaphone,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { apiClient } from "../services/api";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const TABS = [
  { id: "overview",   icon: LayoutDashboard, section: "organisation" },
  { id: "positions",  icon: Briefcase,       section: "organisation" },
  { id: "open",       icon: AlertCircle,     section: "recruitment" },
  { id: "key",        icon: Star,            section: "succession" },
];

const STATUS_CFG = {
  open:             { color: "bg-red-100 text-red-700",    icon: Circle },
  partially_filled: { color: "bg-orange-100 text-orange-700", icon: AlertCircle },
  filled:           { color: "bg-green-100 text-green-700", icon: CheckCircle },
  frozen:           { color: "bg-blue-100 text-blue-700",  icon: Snowflake },
};

const EMP_TYPES = ["full_time", "part_time", "contract", "intern"];

export default function PositionsHub() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "overview";
  const setTab = (tab) => setSearchParams({ tab });
  const sections = [...new Set(TABS.map((t) => t.section))];

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Briefcase className="h-6 w-6 text-slate-600" />
          {t("positions.title")}
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          {t("positions.subtitle")}
        </p>
      </div>

      <div className="border-b">
        {sections.map((section) => (
          <div key={section} className="inline-block mr-6 mb-0">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mr-3">
              {t(`positions.sections.${section}`)}
            </span>
            {TABS.filter((t) => t.section === section).map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setTab(tab.id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors mr-1 ${
                    activeTab === tab.id
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {t(`positions.tabs.${tab.id}`)}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      <div>
        {activeTab === "overview"  && <OverviewTab />}
        {activeTab === "positions" && <PositionsTab />}
        {activeTab === "open"      && <OpenTab />}
        {activeTab === "key"       && <KeyTab />}
      </div>
    </div>
  );
}

// ── Overview ──────────────────────────────────────────────────────────────────

function OverviewTab() {
  const { t } = useTranslation();
  const [overview, setOverview] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ovRes, stRes] = await Promise.all([
        apiClient.get("v1/positions/overview"),
        apiClient.get("v1/positions/stats"),
      ]);
      setOverview(ovRes.data);
      setStats(stRes.data);
    } catch {
      toast.error(t("positions.toasts.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p className="text-muted-foreground">{t("common.loading")}</p>;

  return (
    <div className="space-y-5">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label={t("positions.stats.total")} value={stats.total_positions} icon={Briefcase} color="text-slate-600" />
          <StatCard label={t("positions.stats.open")} value={stats.open_positions} icon={AlertCircle} color="text-red-500" />
          <StatCard label={t("positions.stats.filled")} value={stats.filled_positions} icon={CheckCircle} color="text-green-500" />
          <StatCard label={t("positions.stats.key")} value={stats.key_positions} icon={Star} color="text-yellow-500" />
        </div>
      )}

      {overview && (
        <>
          {/* Global fill rate */}
          <Card>
            <CardContent className="py-4 px-4">
              <div className="flex items-center justify-between mb-2">
                <p className="font-semibold text-sm">{t("positions.overview.fillRate")}</p>
                <span className="text-2xl font-bold text-primary">{overview.fill_rate_pct}%</span>
              </div>
              <Progress value={overview.fill_rate_pct} className="h-2" />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>{t("positions.overview.filled", { count: overview.total_headcount_filled })}</span>
                <span>{t("positions.overview.open", { count: overview.total_open })}</span>
                <span>{t("positions.overview.approved", { count: overview.total_headcount_approved })}</span>
              </div>
            </CardContent>
          </Card>

          {/* Department breakdown */}
          <div className="space-y-2">
            <p className="text-sm font-medium">{t("positions.overview.byDept")}</p>
            {overview.departments.map((dept) => {
              const fillPct = dept.headcount_approved
                ? Math.round((dept.headcount_filled / dept.headcount_approved) * 100)
                : 0;
              return (
                <Card key={dept.department_id}>
                  <CardContent className="py-3 px-4">
                    <div className="flex items-center gap-4">
                      <div className="w-36 flex-shrink-0">
                        <p className="font-medium text-sm truncate">{dept.department_name}</p>
                        <p className="text-xs text-muted-foreground">{t("positions.overview.positionsCount", { count: dept.positions_total })}</p>
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-muted-foreground">
                            {dept.headcount_filled} / {dept.headcount_approved}
                          </span>
                          <span className="font-medium">{fillPct}%</span>
                        </div>
                        <Progress value={fillPct} className="h-1.5" />
                      </div>
                      <div className="flex gap-2 flex-shrink-0 text-xs">
                        {dept.open_positions > 0 && (
                          <span className="bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-medium">
                            {t("positions.overview.openCount", { count: dept.open_positions })}
                          </span>
                        )}
                        {dept.key_positions > 0 && (
                          <span className="bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded-full">
                            ⭐ {dept.key_positions}
                          </span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ── Positions list ─────────────────────────────────────────────────────────────

function PositionsTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [positions, setPositions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [deptFilter, setDeptFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    title: "", code: "", department_id: "none", employment_type: "full_time",
    headcount_approved: 1, grade: "", budget_annual: "", is_key_position: false, notes: "",
  });
  const [saving, setSaving] = useState(false);

  // Assign dialog
  const [assignTarget, setAssignTarget] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [assignForm, setAssignForm] = useState({ employee_id: "", start_date: "", is_acting: false });

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (deptFilter && deptFilter !== "all") params.set("department_id", deptFilter);
    if (statusFilter && statusFilter !== "all") params.set("status", statusFilter);
    const [posRes, deptRes] = await Promise.all([
      apiClient.get(`v1/positions?${params}`),
      apiClient.get("v1/organization-chart/departments/").catch(() => ({ data: [] })),
    ]);
    setPositions(Array.isArray(posRes.data) ? posRes.data : []);
    setDepartments(Array.isArray(deptRes.data) ? deptRes.data : []);
  }, [deptFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const createPosition = async () => {
    if (!form.title.trim()) { toast.error(t("positions.toasts.titleRequired")); return; }
    setSaving(true);
    try {
      await apiClient.post("v1/positions", {
        ...form,
        department_id: (form.department_id && form.department_id !== "none") ? parseInt(form.department_id) : null,
        headcount_approved: parseInt(form.headcount_approved) || 1,
        budget_annual: form.budget_annual ? parseFloat(form.budget_annual) : null,
        code: form.code || null,
      });
      toast.success(t("positions.toasts.created"));
      setShowForm(false);
      setForm({ title: "", code: "", department_id: "none", employment_type: "full_time", headcount_approved: 1, grade: "", budget_annual: "", is_key_position: false, notes: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  const openAssign = async (pos) => {
    setAssignTarget(pos);
    setAssignForm({ employee_id: "", start_date: new Date().toISOString().split("T")[0], is_acting: false });
    if (!employees.length) {
      const res = await apiClient.get("v1/employees/?limit=200");
      setEmployees(res.data.employees || res.data || []);
    }
  };

  const doAssign = async () => {
    if (!assignForm.employee_id || !assignForm.start_date) { toast.error(t("positions.toasts.assignReq")); return; }
    try {
      await apiClient.post(`v1/positions/${assignTarget.id}/assign`, {
        employee_id: parseInt(assignForm.employee_id),
        start_date: assignForm.start_date,
        is_acting: assignForm.is_acting,
      });
      toast.success(t("positions.toasts.assigned"));
      setAssignTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("positions.toasts.assignError"));
    }
  };

  const vacate = async (posId, empId) => {
    try {
      await apiClient.post(`v1/positions/${posId}/vacate`, {
        employee_id: empId,
        end_date: new Date().toISOString().split("T")[0],
      });
      toast.success(t("positions.toasts.vacated"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    }
  };

  const filtered = positions.filter((p) =>
    !search || p.title.toLowerCase().includes(search.toLowerCase()) || p.code?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-center flex-wrap">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input className="pl-8 w-48" placeholder={t("positions.list.search")} value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={deptFilter} onValueChange={setDeptFilter}>
          <SelectTrigger className="w-44"><SelectValue placeholder={t("positions.list.department")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("positions.list.all")}</SelectItem>
            {departments.map((d) => <SelectItem key={d.id} value={String(d.id)}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36"><SelectValue placeholder={t("positions.list.status")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("positions.list.all")}</SelectItem>
            {Object.keys(STATUS_CFG).map((k) => <SelectItem key={k} value={k}>{t(`positions.status.${k}`)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" onClick={load}><RefreshCw className="h-3 w-3 mr-1" />{t("positions.list.refresh")}</Button>
        <Button size="sm" onClick={() => setShowForm(!showForm)} className="ml-auto">
          <Plus className="h-4 w-4 mr-1" /> {t("positions.list.newPos")}
        </Button>
      </div>

      {showForm && (
        <Card className="border-primary/30">
          <CardHeader><CardTitle className="text-sm">{t("positions.list.createTitle")}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder={t("positions.list.titlePh")} value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
              <Input placeholder={t("positions.list.codePh")} value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Select value={form.department_id} onValueChange={(v) => setForm((f) => ({ ...f, department_id: v }))}>
                <SelectTrigger><SelectValue placeholder={t("positions.list.department")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("positions.list.none")}</SelectItem>
                  {departments.map((d) => <SelectItem key={d.id} value={String(d.id)}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={form.employment_type} onValueChange={(v) => setForm((f) => ({ ...f, employment_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EMP_TYPES.map((k) => <SelectItem key={k} value={k}>{t(`positions.empType.${k}`)}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input type="number" placeholder={t("positions.list.headcountPh")} min={1} value={form.headcount_approved} onChange={(e) => setForm((f) => ({ ...f, headcount_approved: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder={t("positions.list.gradePh")} value={form.grade} onChange={(e) => setForm((f) => ({ ...f, grade: e.target.value }))} />
              <Input type="number" placeholder={t("positions.list.budgetPh")} value={form.budget_annual} onChange={(e) => setForm((f) => ({ ...f, budget_annual: e.target.value }))} />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="kp" checked={form.is_key_position} onChange={(e) => setForm((f) => ({ ...f, is_key_position: e.target.checked }))} className="accent-primary" />
              <label htmlFor="kp" className="text-sm">{t("positions.list.keyPosition")}</label>
            </div>
            <Textarea placeholder={t("positions.list.notesPh")} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={2} />
            <div className="flex gap-2">
              <Button size="sm" onClick={createPosition} disabled={saving}>{saving ? t("positions.list.creating") : t("positions.list.create")}</Button>
              <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>{t("common.cancel")}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Assign dialog */}
      {assignTarget && (
        <Card className="border-blue-300 bg-blue-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t("positions.list.assignTitle", { title: assignTarget.title })}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Select value={assignForm.employee_id} onValueChange={(v) => setAssignForm((f) => ({ ...f, employee_id: v }))}>
              <SelectTrigger><SelectValue placeholder={t("positions.list.choosePlaceholder")} /></SelectTrigger>
              <SelectContent>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={String(e.id)}>
                    {e.first_name} {e.last_name}
                    {e.department?.name ? ` — ${e.department.name}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-3">
              <Input type="date" value={assignForm.start_date} onChange={(e) => setAssignForm((f) => ({ ...f, start_date: e.target.value }))} className="flex-1" />
              <label className="flex items-center gap-1.5 text-sm">
                <input type="checkbox" checked={assignForm.is_acting} onChange={(e) => setAssignForm((f) => ({ ...f, is_acting: e.target.checked }))} className="accent-primary" />
                {t("positions.list.acting")}
              </label>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={doAssign}>{t("positions.list.assign")}</Button>
              <Button size="sm" variant="outline" onClick={() => setAssignTarget(null)}>{t("common.cancel")}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {filtered.length === 0 && <p className="text-muted-foreground text-sm">{t("positions.list.noPositions")}</p>}
        {filtered.map((pos) => {
          const cfg = STATUS_CFG[pos.status] || STATUS_CFG.open;
          const Icon = cfg.icon;
          const isExpanded = expanded === pos.id;
          const fillPct = pos.headcount_approved ? Math.round((pos.headcount_filled / pos.headcount_approved) * 100) : 0;

          return (
            <Card key={pos.id}>
              <CardContent className="py-3 px-4">
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-sm">{pos.title}</p>
                      {pos.code && <span className="text-xs text-muted-foreground font-mono">{pos.code}</span>}
                      {pos.is_key_position && <span className="text-xs">⭐</span>}
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${cfg.color}`}>
                        <Icon className="h-3 w-3 inline mr-0.5" />{t(`positions.status.${pos.status}`, { defaultValue: pos.status })}
                      </span>
                      <span className="text-xs text-muted-foreground">{t(`positions.empType.${pos.employment_type}`, { defaultValue: pos.employment_type })}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                      {pos.department?.name && <span>{pos.department.name}</span>}
                      {pos.grade && <span>{t("positions.list.grade", { grade: pos.grade })}</span>}
                      {pos.budget_annual && <span>{t("positions.list.budgetPerYear", { amount: Math.round(pos.budget_annual / 1000) })}</span>}
                    </div>
                  </div>

                  <div className="text-right flex-shrink-0 w-28">
                    <p className="text-xs text-muted-foreground">{t("positions.list.filledOf", { filled: pos.headcount_filled, approved: pos.headcount_approved })}</p>
                    <Progress value={fillPct} className="h-1 mt-1" />
                    {pos.headcount_open > 0 && (
                      <p className="text-xs text-red-600 font-medium mt-0.5">{t("positions.list.openCount", { count: pos.headcount_open })}</p>
                    )}
                  </div>

                  <div className="flex gap-1 flex-shrink-0">
                    {pos.headcount_open > 0 && (
                      <Button size="sm" variant="ghost" onClick={() => recruitForPosition(navigate, pos)} title={t("positions.open.recruit")}>
                        <Megaphone className="h-4 w-4" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => openAssign(pos)} title={t("positions.list.assignTooltip")}>
                      <UserPlus className="h-4 w-4" />
                    </Button>
                    <button
                      onClick={() => setExpanded(isExpanded ? null : pos.id)}
                      className="text-muted-foreground hover:text-foreground p-1"
                    >
                      {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {isExpanded && <PositionDetail posId={pos.id} onVacate={vacate} />}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function PositionDetail({ posId, onVacate }) {
  const { t } = useTranslation();
  const [history, setHistory] = useState([]);

  useEffect(() => {
    apiClient.get(`v1/positions/${posId}/history`).then((r) => setHistory(Array.isArray(r.data) ? r.data : []));
  }, [posId]);

  const active = history.filter((h) => !h.end_date);
  const past = history.filter((h) => h.end_date);

  return (
    <div className="mt-3 pt-3 border-t space-y-2">
      {active.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">{t("positions.detail.currentHolders")}</p>
          {active.map((occ) => (
            <div key={occ.id} className="flex items-center gap-2 text-sm py-1">
              <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                {occ.employee?.first_name?.[0]}
              </div>
              <span className="flex-1">{occ.employee?.first_name} {occ.employee?.last_name}</span>
              {occ.is_acting && <Badge variant="outline" className="text-xs">{t("positions.detail.acting")}</Badge>}
              <span className="text-xs text-muted-foreground">{t("positions.detail.since", { date: occ.start_date })}</span>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 text-xs text-red-500 hover:text-red-700"
                onClick={() => onVacate(posId, occ.employee_id)}
              >
                <UserMinus className="h-3 w-3 mr-1" />{t("positions.detail.vacate")}
              </Button>
            </div>
          ))}
        </div>
      )}
      {active.length === 0 && <p className="text-xs text-muted-foreground italic">{t("positions.detail.noHolder")}</p>}
      {past.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">{t("positions.detail.history")}</p>
          {past.slice(0, 3).map((occ) => (
            <p key={occ.id} className="text-xs text-muted-foreground">
              {occ.employee?.first_name} {occ.employee?.last_name} · {occ.start_date} → {occ.end_date}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Open positions ─────────────────────────────────────────────────────────────

function OpenTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get("v1/positions?status=open&active_only=true")
      .then((r) => setPositions(Array.isArray(r.data) ? r.data : []))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-muted-foreground">{t("common.loading")}</p>;

  return (
    <div className="space-y-4 max-w-2xl">
      <p className="text-sm text-muted-foreground">{t("positions.open.toFill", { count: positions.length })}</p>

      {positions.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500 opacity-60" />
            <p>{t("positions.open.allFilled")}</p>
          </CardContent>
        </Card>
      ) : (
        positions.map((pos) => (
          <Card key={pos.id} className="border-red-200">
            <CardContent className="py-4 px-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-semibold">{pos.title}</p>
                  <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground flex-wrap">
                    {pos.department?.name && <span>{pos.department.name}</span>}
                    {pos.grade && <span>{t("positions.open.grade", { grade: pos.grade })}</span>}
                    <span>{t(`positions.empType.${pos.employment_type}`, { defaultValue: pos.employment_type })}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-lg font-bold text-red-600">{pos.headcount_open}</p>
                    <p className="text-xs text-muted-foreground">{t("positions.open.openPositions")}</p>
                  </div>
                  <Button size="sm" onClick={() => recruitForPosition(navigate, pos)}>
                    <Megaphone className="h-4 w-4 mr-1" />{t("positions.open.recruit")}
                  </Button>
                </div>
              </div>
              {pos.budget_annual && (
                <p className="text-xs text-primary mt-1">
                  {t("positions.open.budget", { amount: Math.round(pos.budget_annual / 1000) })}
                </p>
              )}
              {pos.notes && <p className="text-xs text-muted-foreground mt-1 italic">{pos.notes}</p>}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}

// ── Key positions ──────────────────────────────────────────────────────────────

function KeyTab() {
  const { t } = useTranslation();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get("v1/positions?key_only=true")
      .then((r) => setPositions(Array.isArray(r.data) ? r.data : []))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-muted-foreground">{t("common.loading")}</p>;

  const toggleKey = async (pos) => {
    try {
      await apiClient.patch(`v1/positions/${pos.id}`, { is_key_position: !pos.is_key_position });
      setPositions((prev) =>
        prev.map((p) => p.id === pos.id ? { ...p, is_key_position: !p.is_key_position } : p)
      );
    } catch {
      toast.error(t("common.error"));
    }
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <p className="text-sm text-muted-foreground">
        {t("positions.key.intro", { count: positions.length })}
      </p>

      {positions.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            <Star className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p>{t("positions.key.none")}</p>
            <p className="text-sm">{t("positions.key.markHint")}</p>
          </CardContent>
        </Card>
      ) : (
        positions.map((pos) => {
          const cfg = STATUS_CFG[pos.status] || STATUS_CFG.open;
          return (
            <Card key={pos.id} className="border-yellow-200 bg-yellow-50/30">
              <CardContent className="py-4 px-4 flex items-center gap-4">
                <Star className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-semibold">{pos.title}</p>
                  <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground flex-wrap">
                    {pos.department?.name && <span>{pos.department.name}</span>}
                    <span className={`px-1.5 py-0.5 rounded-full font-medium ${cfg.color}`}>{t(`positions.status.${pos.status}`, { defaultValue: pos.status })}</span>
                    <span>{t("positions.list.filledOf", { filled: pos.headcount_filled, approved: pos.headcount_approved })}</span>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs"
                  onClick={() => toggleKey(pos)}
                >
                  {t("positions.key.remove")}
                </Button>
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

// Soft-link an open position to a new job offer: navigate to the recruitment
// hub's offers tab and pre-fill the create form via router state. No FK is
// stored — the offer simply starts seeded with the position's title. The
// description (which is public on the careers portal) is only seeded with the
// position notes if any; the recruiter / AI generator fills the rest.
function recruitForPosition(navigate, pos) {
  navigate("/recrutement?tab=offres", {
    state: { prefillOffer: { title: pos.title, description: pos.notes || "" } },
  });
}

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <Card>
      <CardContent className="py-3 px-4">
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 ${color}`} />
          <span className="text-xs text-muted-foreground">{label}</span>
        </div>
        <p className="text-2xl font-bold mt-1">{value ?? "—"}</p>
      </CardContent>
    </Card>
  );
}
